"use client";

import { clsx } from "clsx";
import { useState } from "react";

import type { TierId } from "@mw/shared/pricing";

import { CornerMarkerDot } from "../core/CornerMarkerDot";
import { DottedBorder } from "../core/GradientBorder";
import { Row } from "./offers/shared";
import type { ColumnCount } from "./PricingTableShared";
import { PricingTableBody } from "./PricingComparisonTable";
import type { PricingTableColumn, PricingTableSection } from "./PricingComparisonTable";
import { PricingValueRenderer } from "./PricingValueRenderer";
import {
  pricingTableGrid,
  TierHeader,
  MobileTierSelector,
  type TranslatedTierInfo,
  type TranslatedSection,
  type BillingPeriod,
} from "./pricing-table-shared";

// Re-export shared types for consumers
export type { TranslatedTierInfo, TranslatedSection, BillingPeriod } from "./pricing-table-shared";

interface AgentsPricingTableClientProps {
  className?: string;
  id?: string;
  tiers: TranslatedTierInfo[];
  sections: TranslatedSection[];
  billingPeriod?: BillingPeriod;
}

/** Single section with comparison table */
const AgentsSection = ({
  section,
  tiers,
  mobileSelectedTierId,
}: {
  section: TranslatedSection;
  tiers: TranslatedTierInfo[];
  mobileSelectedTierId?: TierId;
}) => {
  const columns: PricingTableColumn<TierId>[] = tiers.map(tier => ({
    id: tier.id,
    mobileLabel: tier.title,
    header: <Row styleVariant='v3' text={tier.title} align='end' />,
  }));

  const columnCount = columns.length as ColumnCount;

  const tableSections: PricingTableSection[] = section.subSections.map(subSection => ({
    label: subSection.label,
    rows: subSection.rows.map((row, rowIndex) => {
      const isLastRow = rowIndex === subSection.rows.length - 1;

      const labelCell = (
        <>
          <Row key={`label-${rowIndex}`} styleVariant='v3' title={row.label} tooltip={row.tooltip} withPadding />
          {!isLastRow && <DottedBorder side='inline' />}
        </>
      );

      const valueCells = row.values.map(valueInfo => {
        const isUnavailable = valueInfo.value.type === "unavailable";
        return (
          <div key={valueInfo.tierId}>
            <Row
              styleVariant='v3'
              align='end'
              text={isUnavailable ? undefined : <PricingValueRenderer value={valueInfo.value} />}
              feature={isUnavailable ? false : undefined}
              withPadding
            />
            {!isLastRow && <DottedBorder side='inline' />}
          </div>
        );
      });

      return { labelCell, valueCells };
    }),
  }));

  const hasTopSubsectionHeader = !!section.subSections[0]?.label;

  return (
    <div>
      <div className='tw-relative tw-border-black/5'>
        {!hasTopSubsectionHeader && <CornerMarkerDot position='topLeft' />}
        {!hasTopSubsectionHeader && <CornerMarkerDot position='topRight' />}
        <CornerMarkerDot position='bottomLeft' />
        <CornerMarkerDot position='bottomRight' />

        {/* Vertical column dividers */}
        <div className='tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-bottom-0 tw-pointer-events-none tw-z-10'>
          <div className={clsx("tw-h-full", pricingTableGrid({ columnCount }))}>
            <div />
            {columns.map((column, index) => (
              <div
                key={column.id}
                className={clsx("tw-border-l tw-relative tw-border-black/5", index > 0 && "max-md:tw-hidden")}
              >
                {!hasTopSubsectionHeader && <CornerMarkerDot position='topLeft' />}
                <CornerMarkerDot position='bottomLeft' />
              </div>
            ))}
          </div>
        </div>

        <PricingTableBody
          columns={columns}
          sections={tableSections}
          mobileSelectedColumnId={mobileSelectedTierId}
          gridClassName={pricingTableGrid({ columnCount })}
          labelColumnClassName='tw-pl-4 lg:tw-pl-10 tw-pr-2 lg:tw-pr-4'
          valueColumnClassName='tw-px-1 lg:tw-px-3'
        />
      </div>
    </div>
  );
};

/**
 * Client component for Agents Pricing Table.
 * Renders pricing comparison tables with sticky tier header.
 */
export const AgentsPricingTableClient = ({
  className,
  id,
  tiers,
  sections,
  billingPeriod = "monthly",
}: AgentsPricingTableClientProps) => {
  const [selectedMobileTierId, setSelectedMobileTierId] = useState<TierId>(tiers[0]?.id ?? "free");

  if (tiers.length === 0) return null;

  return (
    <div id={id} className={clsx("tw-w-full", className)}>
      <MobileTierSelector
        tiers={tiers}
        selectedTierId={selectedMobileTierId}
        onTierChange={setSelectedMobileTierId}
        billingPeriod={billingPeriod}
      />
      <TierHeader tiers={tiers} billingPeriod={billingPeriod} />
      {sections.map(section => (
        <AgentsSection key={section.id} section={section} tiers={tiers} mobileSelectedTierId={selectedMobileTierId} />
      ))}
    </div>
  );
};

export default AgentsPricingTableClient;
